(function () {
  'use strict';

  var MODULE_NAME = '[UpCartInsuranceSync]';
  var RETRY_DELAYS_MS = [180, 650, 1400, 2600, 4200, 6200];
  var COOLDOWN_AFTER_WRITE_MS = 1800;
  var observedHosts = new WeakSet();
  var pendingTimers = [];
  var syncInFlight = false;
  var lastCartWriteAt = 0;
  var internalCartWriteDepth = 0;
  var fetchObserverInstalled = false;

  function normalizeText(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
  }

  function normalizeId(value) {
    if (value === null || value === undefined) return '';
    var idText = String(value).trim();
    if (!idText) return '';
    var matches = idText.match(/\d+/g);
    return matches && matches.length ? matches[matches.length - 1] : idText;
  }

  function toPositiveInt(value) {
    var parsed = parseInt(normalizeId(value), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  function parseFrequencyFromPlanName(name) {
    var normalized = normalizeText(name).toLowerCase();
    if (!normalized) return null;

    var match = normalized.match(/(\d{1,4})\s*(day|days|week|weeks|wk|wks|month|months|mo|mos)?/);
    if (!match) return null;

    var interval = parseInt(match[1], 10);
    if (!Number.isFinite(interval) || interval <= 0) return null;

    var unit = match[2] || 'days';
    if (/week|wk/.test(unit)) return interval * 7;
    if (/month|mo/.test(unit)) return interval * 30;
    return interval;
  }

  function rootPath() {
    var root =
      (window.theme && window.theme.routes && window.theme.routes.root_url) ||
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      '/';
    if (typeof root !== 'string' || !root) return '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function cartEndpoint(path) {
    return rootPath() + path;
  }

  function getInsuranceConfig() {
    if (window.SubscriptionInsuranceCore && window.SubscriptionInsuranceCore.getInsuranceConfig) {
      return window.SubscriptionInsuranceCore.getInsuranceConfig();
    }
    var settings = (window.theme && window.theme.shopSettings) || {};
    var tiers = Array.isArray(settings.shippingInsuranceTiers)
      ? settings.shippingInsuranceTiers
      : [{
          key: 'standard',
          variantId: settings.shippingInsuranceVariantId,
          minSubtotal: 0,
          maxSubtotal: null,
          supportsSubscription: true
        }];
    var legacyVariantIds = settings.shippingInsuranceLegacyVariantIds || [];
    return {
      enabled: Boolean(settings.shippingInsuranceEnabled),
      variantId: toPositiveInt(settings.shippingInsuranceVariantId),
      variantIds: tiers.map(function(tier) {
        return normalizeId(tier.variantId);
      }).concat(legacyVariantIds.map(normalizeId)).filter(Boolean),
      tiers: tiers,
      sellingPlans: settings.insuranceSellingPlans || {}
    };
  }

  function isInsuranceVariantId(value, config) {
    var normalized = normalizeId(value);
    return Boolean(normalized && (config.variantIds || []).map(normalizeId).indexOf(normalized) !== -1);
  }

  function getMerchandiseSubtotal(cart, config) {
    if (window.SubscriptionInsuranceCore && window.SubscriptionInsuranceCore.getMerchandiseSubtotal) {
      return window.SubscriptionInsuranceCore.getMerchandiseSubtotal(cart.items, config);
    }
    return cart.items.reduce(function(total, line) {
      if (isInsuranceVariantId(line.variant_id, config)) return total;
      return total + Number(line.final_line_price || line.line_price || 0);
    }, 0);
  }

  function resolveTier(cart, config) {
    var subtotal = getMerchandiseSubtotal(cart, config);
    if (window.SubscriptionInsuranceCore && window.SubscriptionInsuranceCore.resolveTierForSubtotal) {
      return window.SubscriptionInsuranceCore.resolveTierForSubtotal(subtotal, config);
    }
    return (config.tiers || []).find(function(tier) {
      return (
        subtotal >= Number(tier.minSubtotal || 0) &&
        (tier.maxSubtotal == null || subtotal < Number(tier.maxSubtotal))
      );
    }) || (config.tiers || [])[0] || null;
  }

  function getMappedPlanIdForFrequency(sellingPlans, frequency) {
    if (!sellingPlans || !frequency) return '';
    var direct = sellingPlans[frequency];
    if (direct) return normalizeId(direct);

    var asStringKey = sellingPlans[String(frequency)];
    if (asStringKey) return normalizeId(asStringKey);

    return '';
  }

  function extractLinePlanId(line) {
    var allocation = line && line.selling_plan_allocation;
    if (!allocation) return '';
    var planId =
      (allocation.selling_plan && allocation.selling_plan.id) ||
      allocation.selling_plan_id ||
      line.selling_plan ||
      '';
    return normalizeId(planId);
  }

  function extractLineFrequency(line, config) {
    var allocation = line && line.selling_plan_allocation;
    if (!allocation) return null;

    var planName = allocation.selling_plan && allocation.selling_plan.name ? allocation.selling_plan.name : '';
    var fromName = parseFrequencyFromPlanName(planName);
    if (fromName) return fromName;

    var linePlanId = extractLinePlanId(line);
    if (!linePlanId) return null;

    var entries = Object.entries(config.sellingPlans || {});
    for (var i = 0; i < entries.length; i++) {
      var frequency = parseInt(entries[i][0], 10);
      var mappedPlanId = normalizeId(entries[i][1]);
      if (mappedPlanId && linePlanId === mappedPlanId && Number.isFinite(frequency) && frequency > 0) {
        return frequency;
      }
    }

    return null;
  }

  async function fetchCart() {
    var response = await fetch(cartEndpoint('cart.js'), {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }

    return response.json();
  }

  async function changeLineQuantity(lineKey, quantity) {
    internalCartWriteDepth += 1;
    var response;
    try {
      response = await fetch(cartEndpoint('cart/change.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: quantity
      })
    });
    } finally {
      internalCartWriteDepth = Math.max(0, internalCartWriteDepth - 1);
    }

    if (!response.ok) {
      throw new Error('Failed to update cart line');
    }

    return response.json();
  }

  async function addInsuranceLine(variantId, targetState) {
    var payload = {
      id: variantId,
      quantity: 1
    };

    if (targetState.mode === 'subscription') {
      var numericPlanId = toPositiveInt(targetState.planId);
      payload.selling_plan = numericPlanId || targetState.planId;
    }

    internalCartWriteDepth += 1;
    var response;
    try {
      response = await fetch(cartEndpoint('cart/add.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(payload)
    });
    } finally {
      internalCartWriteDepth = Math.max(0, internalCartWriteDepth - 1);
    }

    if (!response.ok) {
      throw new Error('Failed to add insurance line');
    }

    return response.json();
  }

  function buildTargetInsuranceState(cart, config) {
    var frequencies = [];
    var tier = resolveTier(cart, config);
    if (window.SubscriptionInsuranceCore && window.SubscriptionInsuranceCore.holdClubTier) {
      tier = window.SubscriptionInsuranceCore.holdClubTier(tier, cart.items, config);
    }
    if (!tier) return { mode: 'onetime', variantId: config.variantId };

    for (var i = 0; i < cart.items.length; i++) {
      var line = cart.items[i];
      if (isInsuranceVariantId(line.variant_id, config)) continue;
      if (!line.selling_plan_allocation) continue;

      var frequency = extractLineFrequency(line, config);
      if (frequency) frequencies.push(frequency);
    }

    if (!tier.supportsSubscription || !frequencies.length) {
      return { mode: 'onetime', variantId: toPositiveInt(tier.variantId) };
    }

    frequencies.sort(function (a, b) {
      return a - b;
    });

    var shortestFrequency = frequencies[0];
    var mappedPlanId = getMappedPlanIdForFrequency(config.sellingPlans, shortestFrequency);

    if (!mappedPlanId) {
      return {
        mode: 'subscription',
        variantId: toPositiveInt(tier.variantId),
        frequency: shortestFrequency,
        missingPlan: true
      };
    }

    return {
      mode: 'subscription',
      variantId: toPositiveInt(tier.variantId),
      frequency: shortestFrequency,
      planId: mappedPlanId
    };
  }

  function isInsuranceAlreadyCorrect(insuranceLines, targetState, config) {
    if (!insuranceLines.length) return true;
    if (insuranceLines.length !== 1) return false;

    var currentLine = insuranceLines[0];
    if (currentLine.quantity !== 1) return false;
    if (toPositiveInt(currentLine.variant_id) !== toPositiveInt(targetState.variantId)) return false;

    var isSubscription = Boolean(currentLine.selling_plan_allocation);
    if (targetState.mode === 'onetime') {
      return !isSubscription;
    }

    if (!isSubscription) return false;
    if (!targetState.planId) return true;

    var currentPlanId = extractLinePlanId(currentLine);
    if (currentPlanId && currentPlanId === targetState.planId) {
      return true;
    }

    var currentFrequency = extractLineFrequency(currentLine, config);
    return Boolean(targetState.frequency && currentFrequency && targetState.frequency === currentFrequency);
  }

  async function syncInsuranceToCartSubscriptionState(trigger) {
    var config = getInsuranceConfig();
    if (!config.enabled || !(config.variantIds || []).length) {
      return { changed: false, reason: 'disabled-or-missing-config' };
    }

    var cart = await fetchCart();
    var insuranceLines = cart.items.filter(function (line) {
      return isInsuranceVariantId(line.variant_id, config);
    });

    if (!insuranceLines.length) {
      return { changed: false, reason: 'no-insurance-line' };
    }

    // Owner decision Oct 13: premium ($1,000+) is opt-in. Don't upgrade a lower tier into premium; remove it instead.
    // A premium line the shopper added is left to the normal checks.
    var premiumTier = resolveTier(cart, config);
    if (premiumTier && (premiumTier.key === 'premium' || Number(premiumTier.minSubtotal || 0) >= 100000)) {
      var onlyPremium = insuranceLines.length === 1 &&
        toPositiveInt(insuranceLines[0].variant_id) === toPositiveInt(premiumTier.variantId);
      if (!onlyPremium) {
        for (var p = 0; p < insuranceLines.length; p++) {
          if (insuranceLines[p] && insuranceLines[p].key) await changeLineQuantity(insuranceLines[p].key, 0);
        }
        dispatchCartUpdated();
        return { changed: true, reason: 'premium-opt-in-removed-lower-tier', trigger: trigger };
      }
    }

    var targetState = buildTargetInsuranceState(cart, config);

    if (targetState.mode === 'subscription' && targetState.missingPlan) {
      console.warn(MODULE_NAME, 'Missing insurance selling plan mapping for frequency:', targetState.frequency);
      return { changed: false, reason: 'missing-plan-id' };
    }

    if (isInsuranceAlreadyCorrect(insuranceLines, targetState, config)) {
      return { changed: false, reason: 'already-correct' };
    }

    // Remove the insurance lines one at a time from a fresh read: with a discount code in the cart, Shopify re-keys the
    // remaining lines after each change, so keys from one earlier read go stale after the first removal (Oct 19).
    for (var guard = 0; guard < 6; guard++) {
      var freshCart = await fetchCart();
      var staleLine = freshCart.items.find(function (line) {
        return isInsuranceVariantId(line.variant_id, config);
      });
      if (!staleLine) break;
      await changeLineQuantity(staleLine.key, 0);
    }

    await addInsuranceLine(targetState.variantId, targetState);
    dispatchCartUpdated();

    return {
      changed: true,
      reason: 'updated',
      mode: targetState.mode,
      frequency: targetState.frequency || null,
      trigger: trigger
    };
  }

  function clearPendingTimers() {
    for (var i = 0; i < pendingTimers.length; i++) {
      clearTimeout(pendingTimers[i]);
    }
    pendingTimers = [];
  }

  function dispatchCartUpdated() {
    try {
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: {
            source: 'upcart-insurance-sync'
          }
        })
      );
    } catch (error) {
      // no-op
    }
  }

  function requestUrlFromInput(input) {
    try {
      if (typeof input === 'string') return input;
      if (input && typeof input.url === 'string') return input.url;
    } catch (error) {
      return '';
    }
    return '';
  }

  function isCartMutationRequestUrl(url) {
    if (!url) return false;
    return /\/cart\/(add|change|update)\.js(\?|$)/i.test(url);
  }

  function installFetchObserver() {
    if (fetchObserverInstalled || typeof window.fetch !== 'function') return;
    fetchObserverInstalled = true;

    var originalFetch = window.fetch.bind(window);
    window.fetch = function () {
      var args = Array.prototype.slice.call(arguments);
      var requestUrl = requestUrlFromInput(args[0]);
      var isCartMutation = isCartMutationRequestUrl(requestUrl);
      var isInternalRequest = internalCartWriteDepth > 0;

      return originalFetch.apply(window, args).then(function (response) {
        if (isCartMutation && !isInternalRequest && response && response.ok && !window.__paSkipUpCart) {
          scheduleSync('cart-mutation-observed', true);
        }
        return response;
      });
    };
  }

  async function runSync(trigger, force) {
    if (syncInFlight) return;
    if (!force && Date.now() - lastCartWriteAt < COOLDOWN_AFTER_WRITE_MS) return;

    syncInFlight = true;
    try {
      var result = await syncInsuranceToCartSubscriptionState(trigger);
      if (result.changed) {
        lastCartWriteAt = Date.now();
      }
    } catch (error) {
      console.warn(MODULE_NAME, 'Insurance sync failed:', error);
    } finally {
      syncInFlight = false;
    }
  }

  function scheduleSync(trigger, force) {
    clearPendingTimers();

    for (var i = 0; i < RETRY_DELAYS_MS.length; i++) {
      (function (delay) {
        var timerId = setTimeout(function () {
          pendingTimers = pendingTimers.filter(function (timer) {
            return timer !== timerId;
          });
          runSync(trigger + ':' + delay, Boolean(force));
        }, delay);
        pendingTimers.push(timerId);
      })(RETRY_DELAYS_MS[i]);
    }
  }

  function isSubscriptionInteractionTarget(target) {
    if (!target || !target.closest) return false;

    var control = target.closest('button, [role="button"], a, label, input');
    if (!control) return false;

    if (
      control.closest('[class*="subscription-upgrade-btn"]') ||
      control.closest('[class*="subscription-upgrade"]') ||
      control.closest('[class*="selling-plan"]') ||
      control.closest('[class*="purchase-option"]') ||
      control.closest('[data-upcart-chunk*="subscription"]')
    ) {
      return true;
    }

    var controlText = normalizeText(
      (control.getAttribute && control.getAttribute('aria-label')) || control.textContent || ''
    ).toLowerCase();

    return /subscribe|subscription|delivery every|one[- ]time|purchase options?/.test(controlText);
  }

  function bindShadowRoot(shadowRoot) {
    if (!shadowRoot || shadowRoot.__paInsuranceSyncBound) return;

    shadowRoot.__paInsuranceSyncBound = true;

    function onSubscriptionInteraction(event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      if (!isSubscriptionInteractionTarget(target)) return;
      scheduleSync('subscription-interaction', true);
    }

    shadowRoot.addEventListener('click', onSubscriptionInteraction, true);
    shadowRoot.addEventListener('change', onSubscriptionInteraction, true);
  }

  function observeHost(host) {
    if (!host || !host.shadowRoot || observedHosts.has(host)) return;
    observedHosts.add(host);
    bindShadowRoot(host.shadowRoot);
  }

  function findHost() {
    return document.getElementById('upCart') || document.querySelector('[id*="upcart" i]');
  }

  function boot() {
    installFetchObserver();

    var host = findHost();
    if (host && host.shadowRoot) {
      observeHost(host);
    }

    var bodyObserver = new MutationObserver(function () {
      var nextHost = findHost();
      if (nextHost && nextHost.shadowRoot) {
        observeHost(nextHost);
      }
    });

    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
